import * as helios from "@hyperionbt/helios"
//wraps helios.Program
export default class Program {
    #program

    constructor(parameters = {}) {
        // load all the dependencies
        const allDeps = []

        const depSrcs = new Map()

        function addDepSrcs(dep) {
            depSrcs.set(dep.name, dep.src)

            for (let d of dep.dependencies) {
                addDepSrcs(d)
            }
        }

        for (let d of allDeps) {
            addDepSrcs(d)
        }

        this.#program = helios.Program.new(`spending offer

// Parameters
const PROTOCOL_FEE_PERCENTAGE: Int = 2
const PROTOCOL_FEE_MIN_LOVELACE: Int = 2_000_000
const TREASURY_ADDRESS: Address = Address::new(
  Credential::new_pubkey(PubKeyHash::new(#)), Option[StakingCredential]::None
)

struct Datum {
  offerer_address: Address
  // Can be an AssetClass for a specific token,
  // or a MintingPolicyHash for Gacha minting, etc.
  wanted_asset: ByteArray
  offer_price: Int // per quantity
  wanted_quantity: Int
  is_secondary: Bool
  artist_address: Option[Address]
  edition_royalty: Int
}

enum Redeemer {
  Fulfill
  Close
}

func main(datum: Datum, redeemer: Redeemer, ctx: ScriptContext) -> Bool {
  signed_by_offerer: Bool = datum.offerer_address.credential.switch {
    pk: PubKey => ctx.tx.is_signed_by(pk.hash),
    else => false
  };

  if (signed_by_offerer) {
    true
  } else {
    prev_output: TxOutput = ctx.get_current_input().output;

    // Tight restrictions for ease of reasoning.
    // We can have a future version that allows "fulfilling" cart.
    offer_inputs: []TxInput = ctx.tx.inputs.filter((input: TxInput) -> {
      input.output.address == prev_output.address
    });
    assert(offer_inputs.length == 1, "Can only handle one Offer at once");

    offer_outputs: []TxOutput = ctx.tx.outputs.filter((output: TxOutput) -> {
      output.address == prev_output.address
    });

    redeemer.switch {
      Fulfill => {
        fulfilled_quantity: Int = if (offer_outputs.length == 0) {
          datum.wanted_quantity
        } else if (offer_outputs.length == 1) {
          next_output: TxOutput = offer_outputs.head;
          next_datum: Datum = next_output.datum.switch {
            i: Inline => Datum::from_data(i.data),
            else => error("Invalid offer output: missing inline datum")
          };
          assert(
            next_datum.offerer_address == datum.offerer_address &&
            next_datum.wanted_asset == datum.wanted_asset &&
            next_datum.offer_price == datum.offer_price &&
            // The only thing that can change
            next_datum.wanted_quantity >= 0 &&
            next_datum.wanted_quantity < datum.wanted_quantity &&
            //
            next_datum.is_secondary == datum.is_secondary &&
            next_datum.artist_address == datum.artist_address &&
            next_datum.edition_royalty == datum.edition_royalty,
            "Must handle datum correctly"
          );

          q: Int = datum.wanted_quantity - next_datum.wanted_quantity;

          assert(
            next_output.value.get_lovelace() >=
            prev_output.value.get_lovelace() - (datum.offer_price * q)
          , "Must handle ADA correctly"
          );

          q
        } else {
          error("Cannot produce more than 1 Offer output")
        };

        // Technically, people can create their own Secondary Offer UTxOs
        // that are marked as Primary. We can either:
        // (1) Accept anyway because they go the extra miles
        // (2) Refuse to index and return no such offers to FE
        assert (
          !datum.is_secondary ||
          ctx.tx.outputs.any((output: TxOutput) -> {
            output.address == TREASURY_ADDRESS &&
            output.value.get_lovelace() >=
              Int::max(
                datum.offer_price * fulfilled_quantity * PROTOCOL_FEE_PERCENTAGE / 100,
                PROTOCOL_FEE_MIN_LOVELACE
              )
          }),
          "Must handle protocol fees correctly"
        );

        // Pay reseller fee
        assert(
          datum.artist_address == Option[Address]::None ||
          ctx.tx.outputs.any((output: TxOutput) -> {
            output.address == datum.artist_address.unwrap() &&
            output.value.get_lovelace() >= datum.edition_royalty * fulfilled_quantity
          }),
          "Must handle reseller fees correctly"
        );

        // Pay offerred asset for...
        // Minting policy
        if (datum.wanted_asset.length == 28) {
          mph: MintingPolicyHash = MintingPolicyHash::new(datum.wanted_asset);
          ctx.tx.outputs.any((output: TxOutput) -> {
            output.address == datum.offerer_address &&
            output.value.contains_policy(mph) &&
            output.value.get_policy(mph).fold((total_quantity: Int, _, token_quantity: Int) -> {
              total_quantity + token_quantity
            }, 0) == fulfilled_quantity
          })
        }
        // Asset class
        else {
          asset_class: AssetClass = AssetClass::new(
            MintingPolicyHash::new(datum.wanted_asset.slice(0, 28)),
            datum.wanted_asset.slice(28, datum.wanted_asset.length)
          );
          ctx.tx.outputs.any((output: TxOutput) -> {
            output.address == datum.offerer_address &&
            output.value.get_safe(asset_class) == fulfilled_quantity
          })
        }
      },
      Close => {
        assert(!datum.is_secondary, "Can only refund primary offers");
        assert(offer_outputs.length == 0, "No Offer created");

        ctx.tx.outputs.any((output: TxOutput) -> {
          output.address == datum.offerer_address &&
          output.value == prev_output.value
        })
      }
    }
  }
}
`, Array.from(depSrcs.values()))

        if (Object.keys(parameters).length > 0) {
            this.#program.parameters = parameters
        }
    }

    get name() {
        return this.#program.name
    }

    get paramTypes() {
        return this.#program.paramTypes
    }

    get parameters() {
        return this.#program.parameters
    }

    get types() {
        return this.#program.types
    }

    set parameters(params) {
        this.#program.parameters = params
    }

    compile(optimize = false) {
        return this.#program.compile(optimize)
    }

    evalParam(paramName) {
        return this.#program.evalParam(paramName)
    }
}
